export const WHITE='w';
export const BLACK='b';

export const initialChessBoard=()=>[
  ['br','bn','bb','bq','bk','bb','bn','br'],
  ['bp','bp','bp','bp','bp','bp','bp','bp'],
  [null,null,null,null,null,null,null,null],
  [null,null,null,null,null,null,null,null],
  [null,null,null,null,null,null,null,null],
  [null,null,null,null,null,null,null,null],
  ['wp','wp','wp','wp','wp','wp','wp','wp'],
  ['wr','wn','wb','wq','wk','wb','wn','wr']
];

export const pieceSide=p=>p?p[0]:null;
export const pieceType=p=>p?p[1]:null;

const inside=(r,c)=>r>=0&&r<8&&c>=0&&c<8;

export const cloneBoard=b=>b.map(row=>[...row]);

function findKing(board,side){
  const target=side+'k';

  for(let r=0;r<8;r++){
    for(let c=0;c<8;c++){
      if(board[r][c]===target){
        return [r,c];
      }
    }
  }

  return null;
}

function basicMoves(board,r,c,state={},attackOnly=false){
  const p=board[r][c];

  if(!p)return [];

  const side=pieceSide(p);
  const type=pieceType(p);
  const enemy=side===WHITE?BLACK:WHITE;
  const out=[];

  const push=(rr,cc,special={})=>{
    if(!inside(rr,cc))return;

    const q=board[rr][cc];

    if(!q||pieceSide(q)!==side){
      out.push({
        from:[r,c],
        to:[rr,cc],
        ...special
      });
    }
  };

  if(type==='p'){
    const dir=side===WHITE?-1:1;
    const start=side===WHITE?6:1;
    const promote=side===WHITE?0:7;

    if(attackOnly){
      for(const dc of[-1,1]){
        const rr=r+dir;
        const cc=c+dc;

        if(inside(rr,cc)){
          out.push({
            from:[r,c],
            to:[rr,cc]
          });
        }
      }

      return out;
    }

    if(inside(r+dir,c)&&!board[r+dir][c]){
      push(
        r+dir,
        c,
        r+dir===promote
          ?{promotion:'q'}
          :{}
      );

      if(
        r===start&&
        !board[r+2*dir][c]
      ){
        push(r+2*dir,c,{doublePawn:true});
      }
    }

    for(const dc of[-1,1]){
      const rr=r+dir;
      const cc=c+dc;

      if(!inside(rr,cc))continue;

      if(
        board[rr][cc]&&
        pieceSide(board[rr][cc])===enemy
      ){
        push(
          rr,
          cc,
          rr===promote
            ?{promotion:'q'}
            :{}
        );
      }

      if(
        state.enPassant&&
        state.enPassant[0]===rr&&
        state.enPassant[1]===cc
      ){
        out.push({
          from:[r,c],
          to:[rr,cc],
          enPassant:true
        });
      }
    }

    return out;
  }

  if(type==='n'){
    const ds=[
      [-2,-1],[-2,1],
      [-1,-2],[-1,2],
      [1,-2],[1,2],
      [2,-1],[2,1]
    ];

    for(const[d1,d2]of ds){
      push(r+d1,c+d2);
    }

    return out;
  }

  if(type==='b'||type==='r'||type==='q'){
    const dirs=[];

    if(type==='b'||type==='q'){
      dirs.push(
        [-1,-1],[-1,1],
        [1,-1],[1,1]
      );
    }

    if(type==='r'||type==='q'){
      dirs.push(
        [-1,0],[1,0],
        [0,-1],[0,1]
      );
    }

    for(const[dr,dc]of dirs){
      let rr=r+dr;
      let cc=c+dc;

      while(inside(rr,cc)){
        const q=board[rr][cc];

        if(!q){
          out.push({
            from:[r,c],
            to:[rr,cc]
          });
        }else{
          if(pieceSide(q)!==side){
            out.push({
              from:[r,c],
              to:[rr,cc]
            });
          }

          break;
        }

        rr+=dr;
        cc+=dc;
      }
    }

    return out;
  }

  if(type==='k'){
    for(let dr=-1;dr<=1;dr++){
      for(let dc=-1;dc<=1;dc++){
        if(dr||dc){
          push(r+dr,c+dc);
        }
      }
    }

    if(attackOnly)return out;

    const rights=state.castling||{};

    if(side===WHITE&&r===7&&c===4){
      if(
        rights.wK&&
        !board[7][5]&&
        !board[7][6]&&
        board[7][7]==='wr'
      ){
        out.push({
          from:[7,4],
          to:[7,6],
          castle:'K'
        });
      }

      if(
        rights.wQ&&
        !board[7][1]&&
        !board[7][2]&&
        !board[7][3]&&
        board[7][0]==='wr'
      ){
        out.push({
          from:[7,4],
          to:[7,2],
          castle:'Q'
        });
      }
    }

    if(side===BLACK&&r===0&&c===4){
      if(
        rights.bK&&
        !board[0][5]&&
        !board[0][6]&&
        board[0][7]==='br'
      ){
        out.push({
          from:[0,4],
          to:[0,6],
          castle:'K'
        });
      }

      if(
        rights.bQ&&
        !board[0][1]&&
        !board[0][2]&&
        !board[0][3]&&
        board[0][0]==='br'
      ){
        out.push({
          from:[0,4],
          to:[0,2],
          castle:'Q'
        });
      }
    }

    return out;
  }

  return out;
}

export function squareAttacked(
  board,
  r,
  c,
  bySide,
  state={}
){
  for(let rr=0;rr<8;rr++){
    for(let cc=0;cc<8;cc++){
      const p=board[rr][cc];

      if(!p||pieceSide(p)!==bySide)continue;

      const moves=basicMoves(
        board,
        rr,
        cc,
        state,
        true
      );

      if(
        moves.some(
          m=>
            m.to[0]===r&&
            m.to[1]===c
        )
      ){
        return true;
      }
    }
  }

  return false;
}

export function inCheck(
  board,
  side,
  state={}
){
  const king=findKing(board,side);

  if(!king)return true;

  const enemy=
    side===WHITE
      ?BLACK
      :WHITE;

  return squareAttacked(
    board,
    king[0],
    king[1],
    enemy,
    state
  );
}

export function applyChessMove(
  board,
  move,
  state={}
){
  const next=cloneBoard(board);
  const[
    fr,
    fc
  ]=move.from;

  const[
    tr,
    tc
  ]=move.to;

  const p=next[fr][fc];
  const side=pieceSide(p);

  next[fr][fc]=null;

  if(move.enPassant){
    const capturedRow=
      side===WHITE
        ?tr+1
        :tr-1;

    next[capturedRow][tc]=null;
  }

  next[tr][tc]=
    move.promotion
      ?side+move.promotion
      :p;

  if(move.castle==='K'){
    if(side===WHITE){
      next[7][7]=null;
      next[7][5]='wr';
    }else{
      next[0][7]=null;
      next[0][5]='br';
    }
  }

  if(move.castle==='Q'){
    if(side===WHITE){
      next[7][0]=null;
      next[7][3]='wr';
    }else{
      next[0][0]=null;
      next[0][3]='br';
    }
  }

  return next;
}

function nextState(
  board,
  move,
  state={}
){
  const next={
    castling:{
      wK:state.castling?.wK!==false,
      wQ:state.castling?.wQ!==false,
      bK:state.castling?.bK!==false,
      bQ:state.castling?.bQ!==false
    },
    enPassant:null
  };

  const[
    fr,
    fc
  ]=move.from;

  const[
    tr,
    tc
  ]=move.to;

  const p=board[fr][fc];
  const captured=board[tr][tc];

  if(p==='wk'){
    next.castling.wK=false;
    next.castling.wQ=false;
  }

  if(p==='bk'){
    next.castling.bK=false;
    next.castling.bQ=false;
  }

  if(p==='wr'&&fr===7&&fc===0){
    next.castling.wQ=false;
  }

  if(p==='wr'&&fr===7&&fc===7){
    next.castling.wK=false;
  }

  if(p==='br'&&fr===0&&fc===0){
    next.castling.bQ=false;
  }

  if(p==='br'&&fr===0&&fc===7){
    next.castling.bK=false;
  }

  if(captured==='wr'&&tr===7&&tc===0){
    next.castling.wQ=false;
  }

  if(captured==='wr'&&tr===7&&tc===7){
    next.castling.wK=false;
  }

  if(captured==='br'&&tr===0&&tc===0){
    next.castling.bQ=false;
  }

  if(captured==='br'&&tr===0&&tc===7){
    next.castling.bK=false;
  }

  if(
    pieceType(p)==='p'&&
    Math.abs(tr-fr)===2
  ){
    next.enPassant=[
      (fr+tr)/2,
      fc
    ];
  }

  return next;
}

export function legalChessMoves(
  board,
  side,
  state={}
){
  const out=[];

  for(let r=0;r<8;r++){
    for(let c=0;c<8;c++){
      const p=board[r][c];

      if(!p||pieceSide(p)!==side)continue;

      const moves=basicMoves(
        board,
        r,
        c,
        state,
        false
      );

      for(const move of moves){
        /*
         * 王车易位时：
         * 王不能正在被将军，
         * 也不能经过被攻击格。
         */
        if(move.castle){
          if(inCheck(board,side,state)){
            continue;
          }

          const enemy=
            side===WHITE
              ?BLACK
              :WHITE;

          const row=
            side===WHITE
              ?7
              :0;

          const pass=
            move.castle==='K'
              ?5
              :3;

          if(
            squareAttacked(
              board,
              row,
              pass,
              enemy,
              state
            )
          ){
            continue;
          }
        }

        const nb=applyChessMove(
          board,
          move,
          state
        );

        const ns=nextState(
          board,
          move,
          state
        );

        if(!inCheck(nb,side,ns)){
          out.push({
            ...move,
            nextState:ns
          });
        }
      }
    }
  }

  return out;
}

export function chessGameState(
  board,
  turn,
  state={}
){
  const check=inCheck(
    board,
    turn,
    state
  );

  const moves=legalChessMoves(
    board,
    turn,
    state
  );

  if(moves.length){
    return {
      over:false,
      check,
      winner:null,
      draw:false
    };
  }

  if(check){
    return {
      over:true,
      check:true,
      winner:
        turn===WHITE
          ?BLACK
          :WHITE,
      draw:false,
      reason:'checkmate'
    };
  }

  return {
    over:true,
    check:false,
    winner:null,
    draw:true,
    reason:'stalemate'
  };
}

export const initialChessState=()=>({
  castling:{
    wK:true,
    wQ:true,
    bK:true,
    bQ:true
  },
  enPassant:null
});
